import React, { useState, useEffect } from 'react';
import { pickingTasksAPI, PickingTask, PickingTaskStatus } from '../../services/api';
import {
  CubeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
  MapPinIcon,
  ExclamationCircleIcon,
  PlayIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

interface PickingTasksListProps {
  onSelectTask: (task: PickingTask) => void;
  dealId?: number;
}

const statusConfig: Record<PickingTaskStatus, { label: string; color: string; icon: React.ReactNode }> = {
  TO_PICK: { label: 'To Pick', color: 'bg-gray-100 text-gray-800', icon: <ClockIcon className="w-4 h-4" /> },
  IN_PROGRESS: { label: 'Picking', color: 'bg-blue-100 text-blue-800', icon: <PlayIcon className="w-4 h-4" /> },
  PICKED: { label: 'Picked', color: 'bg-indigo-100 text-indigo-800', icon: <CheckCircleIcon className="w-4 h-4" /> },
  QC_PENDING: { label: 'QC Pending', color: 'bg-yellow-100 text-yellow-800', icon: <ExclamationCircleIcon className="w-4 h-4" /> },
  QC_PASSED: { label: 'QC Passed', color: 'bg-green-100 text-green-800', icon: <CheckCircleIcon className="w-4 h-4" /> },
  QC_FAILED: { label: 'QC Failed', color: 'bg-red-100 text-red-800', icon: <XCircleIcon className="w-4 h-4" /> },
  PACKED: { label: 'Packed', color: 'bg-emerald-100 text-emerald-800', icon: <CubeIcon className="w-4 h-4" /> },
  CANCELLED: { label: 'Cancelled', color: 'bg-gray-200 text-gray-500', icon: <XCircleIcon className="w-4 h-4" /> },
};

export const PickingTasksList: React.FC<PickingTasksListProps> = ({ onSelectTask, dealId }) => {
  const [tasks, setTasks] = useState<PickingTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<PickingTaskStatus | ''>('');

  useEffect(() => {
    loadTasks();
  }, [statusFilter, dealId]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const data = await pickingTasksAPI.getAll({
        status: statusFilter || undefined,
        dealId,
      });
      setTasks(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load picking tasks');
    } finally {
      setLoading(false);
    }
  };

  const getProgress = (task: PickingTask) => {
    if (!task.items || task.items.length === 0) return 0;
    const picked = task.items.reduce((sum, item) => sum + item.pickedQuantity, 0);
    const total = task.items.reduce((sum, item) => sum + item.requiredQuantity, 0);
    return total > 0 ? Math.round((picked / total) * 100) : 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <ExclamationCircleIcon className="w-5 h-5 inline mr-2" />
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-4 mb-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as PickingTaskStatus | '')}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Statuses</option>
          {Object.entries(statusConfig).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>
        <button onClick={loadTasks} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm">
          Refresh
        </button>
      </div>

      {/* Tasks List */}
      {tasks.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <CubeIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No picking tasks found</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {tasks.map((task) => {
            const status = statusConfig[task.status];
            const progress = getProgress(task);
            return (
              <div
                key={task.id}
                onClick={() => onSelectTask(task)}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-semibold text-lg">Task #{task.id}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${status.color}`}>
                        {status.icon} {status.label}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div className="flex items-center gap-2">
                        <CubeIcon className="w-4 h-4" />
                        <span>Order: {task.deal?.orderNumber || `Deal #${task.dealId}`}</span>
                      </div>
                      {task.deal?.client && (
                        <div className="flex items-center gap-2">
                          <UserIcon className="w-4 h-4" />
                          <span>{task.deal.client.name}</span>
                        </div>
                      )}
                      {task.warehouse && (
                        <div className="flex items-center gap-2">
                          <MapPinIcon className="w-4 h-4" />
                          <span>{task.warehouse.name} ({task.warehouse.code})</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500 mb-2">
                      {task._count?.items || task.items?.length || 0} items
                    </div>
                    {task.status !== 'TO_PICK' && task.status !== 'CANCELLED' && (
                      <div className="w-24">
                        <div className="text-xs text-gray-500 mb-1">{progress}% picked</div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-600 rounded-full" style={{ width: `${progress}%` }} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {task.assignedUser && (
                  <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-500">
                    Assigned to: {task.assignedUser.firstName} {task.assignedUser.lastName}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PickingTasksList;

